import React, { useState, useRef, useCallback } from "react";
import {
  MapPin,
  Navigation,
  Info,
  ClipboardList,
  X,
  Building,
} from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "../../components/AdminSharedComponents";
import TextField from "../../components/ui/Form/TextField";
import TextareaField from "../../components/ui/Form/TextareaField";
import DropdownField from "../../components/ui/Form/DropdownField";
import { createCustomIcon, validateCoordinates } from "../ExcavationMap/utils";

const ExcavationManualForm = ({
  formData,
  projects,
  validationErrors,
  handleChange,
  handleCoordinateChange,
  submitting,
  isProjectPreFilled = false,
  hasDraft = false,
  onClearDraft,
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const projectMarkerRef = useRef(null);
  const isUpdatingFromMap = useRef(false);
  const lastProjectIdRef = useRef(null); // Track last zoomed project to prevent loops

  const [manualCoordinates, setManualCoordinates] = useState({
    lat: "",
    lng: "",
  });

  // Status options
  const statusOptions = [
    { value: "Planned", label: "Planned" },
    { value: "Active", label: "Active" },
    { value: "Completed", label: "Completed" },
    { value: "Suspended", label: "Suspended" },
  ];

  // Project options
  const projectOptions = projects.map((project) => ({
    value: project.id,
    label: project.name,
  }));

  // Helper function to create project marker icon with opacity
  const createProjectMarkerIcon = (opacity = 1) => {
    return L.divIcon({
      html: `<div style="
        width: 30px;
        height: 30px;
        background-color: #3b82f6;
        border: 3px solid #ffffff;
        border-radius: 50%;
        box-shadow: 0 2px 12px rgba(0,0,0,0.3);
        position: relative;
        opacity: ${opacity};
        transition: opacity 0.3s ease;
      ">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 12px;
          height: 12px;
          background-color: #ffffff;
          border-radius: 50%;
        "></div>
      </div>`,
      className: "project-location-marker",
      iconSize: [26, 26],
      iconAnchor: [13, 13],
      popupAnchor: [0, -13],
    });
  };

  // Helper function to update project marker opacity
  const updateProjectMarkerOpacity = useCallback((opacity) => {
    if (projectMarkerRef.current && mapInstanceRef.current) {
      const newIcon = createProjectMarkerIcon(opacity);
      projectMarkerRef.current.setIcon(newIcon);
    }
  }, []);

  // Initialize map
  const initializeMap = useCallback(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Default center (Jakarta)
    const defaultCenter = [-6.2088, 106.8456];
    const center = formData.coordinates
      ? [formData.coordinates.lat, formData.coordinates.lng]
      : defaultCenter;

    // Initialize Leaflet map
    mapInstanceRef.current = L.map(mapRef.current).setView(center, 15);

    // Define tile layers
    const cartoDBLayer = L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: "abcd",
        maxZoom: 20,
      }
    );

    const openStreetMapLayer = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }
    );

    // Add CartoDB as default layer
    cartoDBLayer.addTo(mapInstanceRef.current);

    // Add layer control for switching
    const baseLayers = {
      "CartoDB Light": cartoDBLayer,
      OpenStreetMap: openStreetMapLayer,
    };
    L.control.layers(baseLayers).addTo(mapInstanceRef.current);

    // Add existing marker if editing
    if (formData.coordinates) {
      const icon = createCustomIcon(formData.status);
      markerRef.current = L.marker(
        [formData.coordinates.lat, formData.coordinates.lng],
        {
          draggable: true,
          icon: icon,
        }
      ).addTo(mapInstanceRef.current);

      markerRef.current.on("dragend", (e) => {
        const position = e.target.getLatLng();
        isUpdatingFromMap.current = true;
        handleCoordinateChange({
          lat: position.lat,
          lng: position.lng,
        });
      });
    }

    // Add click event to map
    mapInstanceRef.current.on("click", (e) => {
      const { lat, lng } = e.latlng;

      // Remove existing excavation marker
      if (markerRef.current) {
        mapInstanceRef.current.removeLayer(markerRef.current);
      }

      // Update project marker opacity (dibuat samar sebagai patokan, tidak dihapus)
      updateProjectMarkerOpacity(0.35);

      // Add new marker with status-based icon
      const icon = createCustomIcon(formData.status);
      markerRef.current = L.marker([lat, lng], {
        draggable: true,
        icon: icon,
      }).addTo(mapInstanceRef.current);

      markerRef.current.on("dragend", (e) => {
        const position = e.target.getLatLng();
        isUpdatingFromMap.current = true;
        handleCoordinateChange({
          lat: position.lat,
          lng: position.lng,
        });

        // Also update manual coordinates fields immediately
        setManualCoordinates({
          lat: position.lat.toString(),
          lng: position.lng.toString(),
        });
      });

      isUpdatingFromMap.current = true;
      handleCoordinateChange({ lat, lng });
    });
  }, [
    formData.coordinates,
    formData.status,
    handleCoordinateChange,
    updateProjectMarkerOpacity,
  ]);

  // Initialize map on mount
  React.useEffect(() => {
    const timer = setTimeout(() => {
      initializeMap();
    }, 100);

    return () => clearTimeout(timer);
  }, [initializeMap]);

  // Update marker when coordinates change
  React.useEffect(() => {
    if (isUpdatingFromMap.current) {
      isUpdatingFromMap.current = false;
      return;
    }

    if (mapInstanceRef.current && formData.coordinates) {
      // Only set view on first marker (initial placement)
      // After that, let user control zoom/pan freely (no auto-focus)
      if (!markerRef.current) {
        mapInstanceRef.current.setView(
          [formData.coordinates.lat, formData.coordinates.lng],
          15
        );
      }
      // Don't auto-focus if marker already exists - prevent "goyang-goyang"

      // Remove existing marker
      if (markerRef.current) {
        mapInstanceRef.current.removeLayer(markerRef.current);
      }

      // Add new marker at the coordinates
      const icon = createCustomIcon(formData.status);
      markerRef.current = L.marker(
        [formData.coordinates.lat, formData.coordinates.lng],
        {
          draggable: true,
          icon: icon,
        }
      ).addTo(mapInstanceRef.current);

      markerRef.current.on("dragend", (e) => {
        const position = e.target.getLatLng();
        isUpdatingFromMap.current = true;
        handleCoordinateChange({
          lat: position.lat,
          lng: position.lng,
        });
      });

      // Update project marker opacity (dibuat samar karena excavation point sudah di-set)
      updateProjectMarkerOpacity(0.35);
    } else if (mapInstanceRef.current && !formData.coordinates) {
      // Remove marker if coordinates are cleared
      if (markerRef.current) {
        mapInstanceRef.current.removeLayer(markerRef.current);
        markerRef.current = null;
      }

      // Restore project marker opacity (kembali ke normal karena excavation point dihapus)
      updateProjectMarkerOpacity(1);
    }
  }, [
    formData.coordinates,
    formData.status,
    handleCoordinateChange,
    updateProjectMarkerOpacity,
  ]);

  // Update marker icon when status changes
  React.useEffect(() => {
    if (markerRef.current && mapInstanceRef.current && formData.coordinates) {
      const newIcon = createCustomIcon(formData.status);
      markerRef.current.setIcon(newIcon);
    }
  }, [formData.status, formData.coordinates]);

  // Fly to project location when project is selected
  const flyToProjectLocation = useCallback(
    (projectId) => {
      if (!mapInstanceRef.current || !projectId) return;

      // Find the selected project
      const project = projects.find((p) => String(p.id) === String(projectId));
      if (!project || !project.coordinates) return;

      // Parse coordinates
      let coords = null;
      if (
        project.coordinates.coordinates &&
        Array.isArray(project.coordinates.coordinates)
      ) {
        // PostGIS format: {coordinates: [lng, lat]}
        coords = {
          lat: project.coordinates.coordinates[1],
          lng: project.coordinates.coordinates[0],
        };
      } else if (project.coordinates.lat && project.coordinates.lng) {
        // Direct format: {lat, lng}
        coords = {
          lat: project.coordinates.lat,
          lng: project.coordinates.lng,
        };
      }

      if (!coords) return;

      // Remove existing project marker if any
      if (projectMarkerRef.current) {
        mapInstanceRef.current.removeLayer(projectMarkerRef.current);
        projectMarkerRef.current = null;
      }

      // Focus to project location instantly (no animation to prevent "goyang-goyang")
      mapInstanceRef.current.setView([coords.lat, coords.lng], 15);

      // Determine opacity: samar (0.35) jika excavation point sudah di-set, normal (1) jika belum
      const opacity = formData.coordinates ? 0.35 : 1;

      // Add project location marker dengan opacity sesuai state
      const projectIcon = createProjectMarkerIcon(opacity);
      projectMarkerRef.current = L.marker([coords.lat, coords.lng], {
        icon: projectIcon,
        zIndexOffset: 1000,
      }).addTo(mapInstanceRef.current);

      // Add popup to project marker
      projectMarkerRef.current.bindPopup(
        `<div class="p-3">
        <h4 class="font-semibold text-sm text-blue-700 mb-2">📍 Project Location</h4>
        <p class="text-sm text-gray-900 font-medium mb-1">${project.name}</p>
        <p class="text-xs text-gray-500 mb-2">Lat: ${coords.lat.toFixed(
          6
        )}, Lng: ${coords.lng.toFixed(6)}</p>
        <p class="text-xs text-orange-600 mt-2 pt-2 border-t border-gray-200">💡 Klik map untuk set excavation point di sekitar lokasi ini</p>
      </div>`,
        { closeButton: false }
      );

      // Auto-open popup for 3 seconds
      projectMarkerRef.current.openPopup();
      setTimeout(() => {
        if (projectMarkerRef.current) {
          projectMarkerRef.current.closePopup();
        }
      }, 3000);
    },
    [projects, formData.coordinates]
  );

  // Trigger flyToProjectLocation when project changes (prevent infinite loop)
  React.useEffect(() => {
    if (formData.project_id && mapInstanceRef.current) {
      // Only fly if project changed (prevent infinite loop)
      if (lastProjectIdRef.current !== formData.project_id) {
        flyToProjectLocation(formData.project_id);
        lastProjectIdRef.current = formData.project_id;
      }
    }
  }, [formData.project_id, flyToProjectLocation]);

  // Sync manualCoordinates with formData.coordinates
  React.useEffect(() => {
    if (formData.coordinates && !isUpdatingFromMap.current) {
      setManualCoordinates({
        lat: formData.coordinates.lat.toString(),
        lng: formData.coordinates.lng.toString(),
      });
    }
  }, [formData.coordinates]);

  // Handle manual coordinate input
  const handleManualCoordinateChange = (e) => {
    const { name, value } = e.target;
    setManualCoordinates((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Apply manual coordinates
  const applyManualCoordinates = () => {
    const lat = parseFloat(manualCoordinates.lat);
    const lng = parseFloat(manualCoordinates.lng);

    const validatedCoords = validateCoordinates({ lat, lng });
    if (!validatedCoords) {
      alert("Please enter valid coordinates");
      return;
    }

    handleCoordinateChange(validatedCoords);

    // Update map - focus instantly, preserve zoom
    if (mapInstanceRef.current) {
      // Focus to coordinates instantly, preserve user's zoom level
      const currentZoom = mapInstanceRef.current.getZoom();
      mapInstanceRef.current.setView(
        [validatedCoords.lat, validatedCoords.lng],
        currentZoom
      );

      // Remove existing excavation marker
      if (markerRef.current) {
        mapInstanceRef.current.removeLayer(markerRef.current);
      }

      // Update project marker opacity (dibuat samar sebagai patokan, tidak dihapus)
      updateProjectMarkerOpacity(0.35);

      // Add new marker with status-based icon
      const icon = createCustomIcon(formData.status);
      markerRef.current = L.marker([validatedCoords.lat, validatedCoords.lng], {
        draggable: true,
        icon: icon,
      }).addTo(mapInstanceRef.current);

      markerRef.current.on("dragend", (e) => {
        const position = e.target.getLatLng();
        isUpdatingFromMap.current = true;
        handleCoordinateChange({
          lat: position.lat,
          lng: position.lng,
        });

        // Also update manual coordinates fields immediately
        setManualCoordinates({
          lat: position.lat.toString(),
          lng: position.lng.toString(),
        });
      });
    }

    // Keep manual input filled with applied coordinates
    setManualCoordinates({
      lat: validatedCoords.lat.toString(),
      lng: validatedCoords.lng.toString(),
    });
  };

  // Clear coordinates
  const clearCoordinates = () => {
    handleCoordinateChange(null);
    setManualCoordinates({ lat: "", lng: "" });

    // Remove excavation marker
    if (markerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }

    // Re-show project marker if project is selected
    if (formData.project_id && mapInstanceRef.current) {
      // Reset to allow re-zoom
      lastProjectIdRef.current = null;
      flyToProjectLocation(formData.project_id);
    }
  };

  return (
    <div className="space-y-8">
      {/* Draft Restored Notification - Only show if has valuable data */}
      {hasDraft &&
        !isProjectPreFilled &&
        (formData.name || formData.coordinates) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  Draft tersimpan dikembalikan
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  {formData.coordinates && formData.name
                    ? "Lokasi dan informasi point telah dipulihkan"
                    : formData.coordinates
                    ? "Lokasi point telah dipulihkan"
                    : "Informasi point telah dipulihkan"}
                </p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={onClearDraft}
              disabled={submitting}
              className="flex-shrink-0"
            >
              <X className="h-4 w-4 mr-1" />
              Mulai Baru
            </Button>
          </div>
        )}

      {/* Step 1: Project Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <Building className="h-5 w-5 text-primary" />
            Select Project
          </h2>
        </div>

        <div className="p-5">
          {isProjectPreFilled && (
            <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Project sudah dipilih otomatis
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Anda sedang menambah titik galian untuk project yang dipilih
                  dari map
                </p>
              </div>
            </div>
          )}

          <DropdownField
  label="Project"
  name="project_id"
  options={projectOptions}
  value={formData.project_id}
  onChange={(e) => {
    console.log("🧩 selected project:", e.target.value);
    handleChange(e);
  }}
  placeholder="Select project"
  required
  error={validationErrors.project_id}
/>


          {formData.project_id &&
            projects.find((p) => String(p.id) === String(formData.project_id))
              ?.coordinates && (
              <div className="mt-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <MapPin className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">
                    Map akan menampilkan lokasi project
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Klik map untuk set excavation point di sekitar marker biru
                  </p>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Step 2: Set Location on Map */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <MapPin className="h-5 w-5 text-primary" />
            Excavation Location
          </h2>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Sidebar: Manual Coordinates */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">
                      Manual Coordinates
                    </h4>
                    {formData.coordinates && (
                      <Button
                        type="button"
                        variant="secondary"
                        className="text-xs px-3 py-1.5 h-auto"
                        onClick={clearCoordinates}
                        disabled={submitting}
                        icon={X}
                      >
                        Clear
                      </Button>
                    )}
                  </div>

                  <div className="space-y-3">
                    <TextField
                      label="Latitude"
                      name="lat"
                      type="number"
                      step="any"
                      value={manualCoordinates.lat}
                      onChange={handleManualCoordinateChange}
                      placeholder="e.g., -6.2088"
                      disabled={submitting}
                    />
                    <TextField
                      label="Longitude"
                      name="lng"
                      type="number"
                      step="any"
                      value={manualCoordinates.lng}
                      onChange={handleManualCoordinateChange}
                      placeholder="e.g., 106.8456"
                      disabled={submitting}
                    />
                  </div>

                  <Button
                    type="button"
                    variant="secondary"
                    onClick={applyManualCoordinates}
                    className="w-full"
                    icon={Navigation}
                    disabled={
                      submitting ||
                      !manualCoordinates.lat ||
                      !manualCoordinates.lng
                    }
                  >
                    {formData.coordinates
                      ? "Update Coordinates"
                      : "Apply Coordinates"}
                  </Button>
                </div>
              </div>

              <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p>
                    <strong>How to set location:</strong>
                  </p>
                  <ul className="list-disc list-inside ml-2 text-xs">
                    <li>Click anywhere on the map</li>
                    <li>Enter coordinates manually</li>
                    <li>Drag the marker to adjust</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right: Map */}
            <div className="lg:col-span-2">
              <div className="border border-gray-300 rounded-lg overflow-hidden shadow-inner">
                <div
                  ref={mapRef}
                  className="w-full h-[400px] lg:h-[500px] rounded-lg"
                  style={{ zIndex: 0 }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 3: Excavation Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <ClipboardList className="h-5 w-5 text-primary" />
            Excavation Details
          </h2>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Excavation Name */}
            <div className="md:col-span-2">
              <TextField
                label="Excavation Point Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter excavation point name"
                required
                maxLength={100}
                error={validationErrors.name}
                showCharCount={true}
                disabled={submitting}
              />
            </div>

            {/* Status */}
            <div>
              <DropdownField
                label="Status"
                name="status"
                options={statusOptions}
                value={formData.status}
                onChange={handleChange}
                placeholder="Select status"
                required
                disabled={submitting}
              />
            </div>

            {/* Depth */}
            <div>
              <TextField
                label="Depth (meters)"
                name="depth"
                type="number"
                value={formData.depth}
                onChange={handleChange}
                placeholder="Enter depth in meters"
                step="0.1"
                min="0"
                error={validationErrors.depth}
                disabled={submitting}
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <TextareaField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter excavation description (optional)"
                rows={3}
                maxLength={500}
                error={validationErrors.description}
                showCharCount={true}
                disabled={submitting}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcavationManualForm;
